import React, { useState, useEffect } from 'react';
import { AlertTriangle, Bell, Clock, Coffee, FileText, RefreshCw, Shield } from 'lucide-react';
import { api } from '../../../utils/api';
import { toast } from '../../ui/simple-toaster';
import { formatDuration } from '../../../utils/formatters';

const BusinessRulesTab = ({
  clientConfig, setClientConfig,
  statutoryRounding, setStatutoryRounding,
  configLoading, onSave,
  clientId
}) => {
  return (
    <div className="space-y-4 sm:space-y-6 max-w-3xl">
      {/* Legal Context */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-2xl p-4 sm:p-6">
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-blue-400" />
          Important: Business Rules vs HVN Law
        </h3>
        <div className="space-y-3 text-sm text-slate-300">
          <p>
            <span className="text-amber-400 font-medium">FRHV Vehicles (GVM &ge; 12t):</span> Must comply with Heavy Vehicle National Law (HVNL).
            Work/rest limits are prescribed by law and cannot be changed here.
          </p>
          <p>
            <span className="text-blue-400 font-medium">Business Rules (GVM &lt; 12t):</span> These configurable limits apply to HV/LV vehicles.
            While not covered by HVNL, employers have WHS/OHS duty of care obligations to manage driver fatigue.
          </p>
          <p className="text-xs text-slate-400 italic">
            Note: Work Health &amp; Safety (WHS) / Occupational Health &amp; Safety (OHS) laws require employers to eliminate or minimise
            risks from fatigue, even for non-FRHV vehicles.
          </p>
        </div>
      </div>

      {/* Work Limits */}
      <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-4 sm:p-6">
        <h2 className="text-base sm:text-lg font-semibold text-white mb-2 flex items-center gap-2">
          <Clock className="w-5 h-5 text-emerald-400" />
          Work Limits
        </h2>
        <p className="text-slate-400 text-xs sm:text-sm mb-6">Maximum work duration before a rest break is required.</p>

        <div className="space-y-5 sm:space-y-6">
          <ConfigSlider
            label="Maximum Continuous Work"
            help="Driver must take a rest break after this duration of work"
            min={1800} max={43200} step={900}
            value={clientConfig.work_limit_seconds}
            onChange={(v) => setClientConfig(prev => ({ ...prev, work_limit_seconds: v }))}
            testId="work-limit-slider"
          />
          <ConfigSlider
            label="Warning Before Work Limit"
            help="Alert driver this long before reaching the work limit"
            min={60} max={1800} step={60}
            value={clientConfig.warning_before_seconds}
            onChange={(v) => setClientConfig(prev => ({ ...prev, warning_before_seconds: v }))}
            testId="warning-before-slider"
          />
        </div>
      </div>

      {/* Rest Requirements */}
      <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-4 sm:p-6">
        <h2 className="text-base sm:text-lg font-semibold text-white mb-2 flex items-center gap-2">
          <Coffee className="w-5 h-5 text-blue-400" />
          Rest Requirements
        </h2>
        <p className="text-slate-400 text-xs sm:text-sm mb-6">Minimum rest duration to reset work counters.</p>

        <div className="space-y-5 sm:space-y-6">
          <ConfigSlider
            label="Minimum Rest Break Duration"
            help="Rest break must be at least this long to reset work counter"
            min={300} max={7200} step={300}
            value={clientConfig.rest_requirement_seconds}
            onChange={(v) => setClientConfig(prev => ({ ...prev, rest_requirement_seconds: v }))}
            testId="rest-requirement-slider"
          />
          <ConfigSlider
            label="Alert Before Can Start Work"
            help="Notify driver this long before their rest requirement is met"
            min={60} max={1800} step={60}
            value={clientConfig.alert_before_start_work_seconds}
            onChange={(v) => setClientConfig(prev => ({ ...prev, alert_before_start_work_seconds: v }))}
            testId="alert-before-start-slider"
          />
        </div>
      </div>

      {/* Statutory Rounding Toggle */}
      <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-white font-medium flex items-center gap-2">
              <Clock className="w-4 h-4 text-violet-400" />
              Statutory 15-Minute Rounding
            </h3>
            <p className="text-slate-400 text-xs mt-1">
              WORK rounded up, REST rounded down to 15-min blocks. Display &amp; PDF only — compliance uses actual times.
            </p>
          </div>
          <button
            onClick={async () => {
              try {
                const newVal = !statutoryRounding;
                await api('/client-settings', {
                  method: 'PUT',
                  body: JSON.stringify({ statutory_rounding: newVal })
                });
                setStatutoryRounding(newVal);
                toast.success(`Statutory rounding ${newVal ? 'enabled' : 'disabled'}`);
              } catch (err) {
                toast.error(err.message || 'Failed to update setting');
              }
            }}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
              statutoryRounding ? 'bg-emerald-500' : 'bg-slate-600'
            }`}
            data-testid="statutory-rounding-toggle"
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
              statutoryRounding ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>
      </div>

      {/* Alert Configuration */}
      <AlertConfigSection clientId={clientId} />

      {/* Save Button */}
      <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-white font-medium">Save Changes</h3>
            <p className="text-slate-400 text-xs mt-1">Changes apply to all non-FRHV vehicles</p>
          </div>
          <button
            onClick={onSave}
            disabled={configLoading}
            className="px-5 sm:px-6 py-2.5 sm:py-3 bg-emerald-500 text-white rounded-xl font-semibold hover:bg-emerald-600 disabled:opacity-50 flex items-center gap-2 text-sm sm:text-base"
            data-testid="save-config-btn"
          >
            {configLoading ? (
              <><RefreshCw className="w-4 h-4 animate-spin" /> Saving...</>
            ) : (
              'Save Configuration'
            )}
          </button>
        </div>
      </div>

      {/* Legal Reference Notes */}
      <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-4 sm:p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-slate-400" />
          Legal Reference Notes
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
            <h4 className="text-amber-400 font-medium mb-3">Consider HVN Law</h4>
            <ul className="text-sm text-slate-300 space-y-2">
              {[
                'What is commonly understood as being fatigued or unfit to drive',
                'The causes of fatigue or being unfit to drive',
                'The signs of fatigue or being unfit to drive',
                'Any relevant body of knowledge',
                'Any other matter set out in the national regulations'
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="text-amber-400 mt-1">&bull;</span> {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4">
            <h4 className="text-blue-400 font-medium mb-3">Consider WHS/OHS Laws</h4>
            <ul className="text-sm text-slate-300 space-y-2">
              <li className="flex items-start gap-2"><span className="text-blue-400 mt-1">&bull;</span> Code of Practice – Managing Risk</li>
              <li className="flex items-start gap-2"><span className="text-blue-400 mt-1">&bull;</span> Code of Practice – Fatigue</li>
            </ul>
            <p className="text-xs text-slate-500 mt-3 italic">
              WHS/OHS laws apply to all workers, regardless of vehicle type.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const AlertConfigSection = ({ clientId }) => {
  const [alertConfig, setAlertConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    api(`/clients/${clientId}/alert-config`).then(cfg => {
      if (typeof cfg.recipients === 'string') cfg.recipients = [cfg.recipients];
      setAlertConfig(cfg);
    }).catch(() => {});
  }, [clientId]);

  const saveAlertConfig = async () => {
    if (!alertConfig || !clientId) return;
    setSaving(true);
    try {
      await api(`/clients/${clientId}/alert-config`, {
        method: 'PUT',
        body: JSON.stringify(alertConfig)
      });
      toast.success('Alert configuration saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save alert config');
    }
    setSaving(false);
  };

  if (!alertConfig) return null;

  const toggleConstraint = (c) => {
    const current = alertConfig.constraints || [];
    const updated = current.includes(c) ? current.filter(x => x !== c) : [...current, c];
    setAlertConfig(prev => ({ ...prev, constraints: updated }));
  };

  const toggleRecipient = (r) => {
    const current = alertConfig.recipients || [];
    const updated = current.includes(r) ? current.filter(x => x !== r) : [...current, r];
    setAlertConfig(prev => ({ ...prev, recipients: updated }));
  };

  const allConstraints = [
    { id: 'CONTINUOUS', label: 'Continuous Work', desc: 'Alert when approaching max continuous work' },
    { id: 'ONE_AM', label: '1AM Night Rest', desc: 'FRHV: Night rest deadline' },
    { id: 'ROLL_24H', label: '24h Rolling Cap', desc: 'FRHV: 24-hour work cap' },
    { id: 'ROLL_7D', label: '7-Day Rolling Cap', desc: 'FRHV: 7-day work cap' },
    { id: 'ROLL_14D', label: '14-Day Rolling Cap', desc: 'FRHV: 14-day work cap' },
    { id: 'NIGHT_REST_OVERDUE', label: 'Night Rest Overdue', desc: 'No qualifying night rest in 24h' },
    { id: 'LONG_REST_OVERDUE', label: 'Long Rest Overdue', desc: 'No 24h rest in 7 days' },
    { id: 'BIZ_WORK_LIMIT', label: 'Business Work Limit', desc: 'Business rules work limit' }
  ];

  const allRecipients = [
    { id: 'DRIVER', label: 'Driver' },
    { id: 'RECORD_KEEPER', label: 'Record Keeper' },
    { id: 'SCHEDULER', label: 'Scheduler' },
    { id: 'CLIENT_ADMIN', label: 'Admin' }
  ];

  return (
    <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-4 sm:p-6">
      <h2 className="text-base sm:text-lg font-semibold text-white mb-2 flex items-center gap-2">
        <Bell className="w-5 h-5 text-amber-400" />
        Alert Configuration
      </h2>
      <p className="text-slate-400 text-xs sm:text-sm mb-6">Configure when and who receives compliance alerts.</p>

      <div className="space-y-6">
        {/* Business Layer for FRHV */}
        <div className="flex items-center justify-between p-3 rounded-xl bg-slate-700/30 border border-slate-600/50">
          <div>
            <p className="text-white text-sm font-medium flex items-center gap-2">
              <Shield className="w-4 h-4 text-emerald-400" />
              Apply Business Rules to FRHV Vehicles
            </p>
            <p className="text-slate-400 text-xs mt-1">
              FRHV drivers will receive business rule warnings BEFORE legal thresholds.
            </p>
          </div>
          <button
            onClick={() => setAlertConfig(prev => ({
              ...prev,
              business_layer_frhv_enabled: !prev.business_layer_frhv_enabled
            }))}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
              alertConfig.business_layer_frhv_enabled ? 'bg-emerald-500' : 'bg-slate-600'
            }`}
            data-testid="frhv-business-layer-toggle"
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
              alertConfig.business_layer_frhv_enabled ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>

        {/* Alert Reference */}
        <div>
          <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-2">Alert Reference Threshold</label>
          <div className="flex gap-3">
            {['SHADOW', 'LAW'].map(ref => (
              <button
                key={ref}
                onClick={() => setAlertConfig(prev => ({ ...prev, alert_reference: ref }))}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  alertConfig.alert_reference === ref
                    ? (ref === 'SHADOW' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/50' : 'bg-red-500/20 text-red-400 border border-red-500/50')
                    : 'bg-slate-700 text-slate-400 border border-slate-600'
                }`}
                data-testid={`alert-ref-${ref.toLowerCase()}`}
              >
                {ref === 'SHADOW' ? 'Shadow (Early)' : 'Law (Strict)'}
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-1">Shadow: alert before legal limit. Law: alert at legal limit.</p>
        </div>

        {/* Lead Time */}
        <div>
          <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-2">
            Lead Time: {alertConfig.lead_time_minutes || 5} minutes
          </label>
          <input
            type="range"
            min={1} max={30} step={1}
            value={alertConfig.lead_time_minutes || 5}
            onChange={(e) => setAlertConfig(prev => ({ ...prev, lead_time_minutes: parseInt(e.target.value) }))}
            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
            data-testid="lead-time-slider"
          />
          <p className="text-xs text-slate-500 mt-1">How many minutes before a threshold to send the alert.</p>
        </div>

        {/* Monitored Rules */}
        <div>
          <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-3">Monitored Rules</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {allConstraints.map(c => (
              <label
                key={c.id}
                className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${
                  (alertConfig.constraints || []).includes(c.id)
                    ? 'bg-emerald-500/10 border border-emerald-500/30'
                    : 'bg-slate-700/30 border border-slate-600/30 hover:border-slate-500/50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={(alertConfig.constraints || []).includes(c.id)}
                  onChange={() => toggleConstraint(c.id)}
                  className="w-4 h-4 rounded border-slate-600"
                  data-testid={`constraint-${c.id.toLowerCase()}`}
                />
                <div>
                  <span className="text-white text-sm">{c.label}</span>
                  <p className="text-slate-500 text-xs">{c.desc}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Recipients */}
        <div>
          <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-3">Alert Recipients</label>
          <div className="flex flex-wrap gap-2">
            {allRecipients.map(r => (
              <button
                key={r.id}
                onClick={() => toggleRecipient(r.id)}
                className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                  (alertConfig.recipients || []).includes(r.id)
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50'
                    : 'bg-slate-700 text-slate-400 border border-slate-600'
                }`}
                data-testid={`recipient-${r.id.toLowerCase()}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {/* Save Alert Config */}
        <button
          onClick={saveAlertConfig}
          disabled={saving}
          className="px-5 py-2.5 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 disabled:opacity-50 flex items-center gap-2 text-sm"
          data-testid="save-alert-config-btn"
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
          {saving ? 'Saving...' : 'Save Alert Configuration'}
        </button>
      </div>
    </div>
  );
};

const ConfigSlider = ({ label, help, min, max, step, value, onChange, testId }) => (
  <div>
    <label className="block text-xs sm:text-sm font-medium text-slate-300 mb-2">{label}</label>
    <div className="flex items-center gap-3 sm:gap-4">
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
        data-testid={testId}
      />
      <span className="text-white font-medium w-16 sm:w-24 text-right text-sm sm:text-base">
        {formatDuration(value)}
      </span>
    </div>
    <p className="text-xs text-slate-500 mt-1">{help}</p>
  </div>
);

export default BusinessRulesTab;
